import React, { useCallback, useEffect, useState } from 'react';
import { ClickAwayListener, FormControl, OutlinedInput } from '@material-ui/core';
import { trim } from 'lodash';
import PropTypes from 'prop-types';
import { Range as SlateRange } from 'slate';
import { ReactEditor, useSlateSelection, useSlateStatic } from 'slate-react';

import { SLATE_TEXT_FORMATS } from 'src/lib/slate';

import { insertLink } from '../../../../utils';

import { useSetupFloating } from './useSetupFloating';
import { useStyles } from './useStyles';

export const LinkInsertPopup = ({ onClose }) => {
  const styles = useStyles();

  const [value, setValue] = useState('');
  const editor = useSlateStatic();
  const selection = useSlateSelection();

  const [floatingElement, setFloatingElement] = useState(null);
  const [reference, setReference] = useState(null);

  useEffect(() => {
    const currentSelection = editor.selection || editor.getPreviousSelection();

    // Resets when the selection has a length of 0
    if (!currentSelection || SlateRange.isCollapsed(currentSelection)) {
      setReference(null);
      onClose();
      return;
    }

    if (!ReactEditor.hasRange(editor, currentSelection)) {
      setReference(null);
      onClose();
      return;
    }

    const range = ReactEditor.toDOMRange(editor, currentSelection);
    setReference(range);

    // Find the current URL value
    const selectionPath = selection.anchor.path;
    let currentChild = editor.children[selectionPath[0]];
    let linkChild;

    for (let i = 1; i < selectionPath.length; i++) {
      if (currentChild.type === SLATE_TEXT_FORMATS.LINK) {
        linkChild = currentChild;
        break;
      }
      currentChild = currentChild.children[selectionPath[i]];
    }

    setValue(linkChild?.url);
  }, [editor, selection, onClose]);

  const { floatingStyles, isPositioned } = useSetupFloating(floatingElement, reference);

  const handleEnterPress = (event) => {
    if (event.key === 'Enter' && !!trim(value)) {
      editor.restoreSelectionFromPrevious();
      insertLink(editor, value);
      onClose();
    }
  };

  const handleChange = useCallback((e) => {
    setValue(e.target.value);
  }, []);

  if (!reference) {
    return null;
  }

  return (
    <ClickAwayListener
      onClickAway={onClose}
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
    >
      <FormControl
        className={styles.root}
        ref={setFloatingElement}
        style={floatingStyles}
      >
        {/* Render input only after "floating-ui" finished positioning, so `autoFocus` won't be causing any scroll events in parent Editor */}
        {isPositioned && (
          <OutlinedInput
            className={styles.input}
            value={value}
            onChange={handleChange}
            placeholder="Insert a URL"
            onKeyDown={handleEnterPress}
            autoFocus
          />
        )}
      </FormControl>
    </ClickAwayListener>
  );
};

LinkInsertPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
};
